import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CircularProgress,
  Autocomplete,
  Chip,
  Typography,
  Avatar
} from '@mui/material';
import { apiService } from '../../services/api';
import { CreateIssueData, Epic, Issue, IssueType, Priority, User, Sprint } from '../../types';
import { SmartToy as SmartToyIcon, Person as PersonIcon } from '@mui/icons-material';

interface CreateIssueModalProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  defaultValues?: Partial<CreateIssueData>;
  onCreated?: (issue: Issue) => void;
  availableUsers?: User[];
  epics?: Epic[];
  boardType?: 'kanban' | 'sprint' | 'scrum' | 'bau';
}

const CreateIssueModal: React.FC<CreateIssueModalProps> = ({
  open,
  onClose,
  projectId,
  defaultValues,
  onCreated,
  availableUsers: providedUsers,
  epics: providedEpics,
  boardType
}) => {
  console.log('🔍 CreateIssueModal props:', {
    providedUsers,
    projectId,
    boardType
  });
  const [users, setUsers] = useState<User[]>(providedUsers || []);
  const [epics, setEpics] = useState<Epic[]>(providedEpics || []);
  const [loadingLists, setLoadingLists] = useState(false);
  const [sprints, setSprints] = useState<Sprint[]>([]);
  // Lazy-loaded parent issues (stories/tasks/subtasks)
  const [parentOptions, setParentOptions] = useState<Issue[]>([]);
  const [parentSearch, setParentSearch] = useState('');
  const [parentPage, setParentPage] = useState(1);
  const [parentHasMore, setParentHasMore] = useState(true);
  const [parentLoading, setParentLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [genLoading, setGenLoading] = useState(false);
  const [form, setForm] = useState<CreateIssueData>({
    title: '',
    description: '',
    type: IssueType.STORY,
    priority: Priority.MEDIUM,
    projectId,
    assigneeId: '',
    storyPoints: undefined,
    originalEstimate: undefined,
    labels: [],
    dueDate: '',
    customFields: {}
  });

  useEffect(() => {
    const values = defaultValues || {};
    // Clear invalid sprint IDs
    if (values.sprintId === 'default-sprint') {
      values.sprintId = undefined;
    }
    setForm(prev => ({
      ...prev,
      projectId,
      ...values
    }));
  }, [projectId, defaultValues]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoadingLists(true);
        // Use provided users if available, otherwise fetch project assignments
        if (providedUsers && providedUsers.length > 0) {
          console.log('🔍 CreateIssueModal: Using provided users (board members):', providedUsers);
          setUsers(providedUsers);
        } else {
          const assignments = await apiService.getProjectAssignments(projectId);
          const projectUsers = assignments.map((a: any) => a.user).filter(Boolean);
          console.log('🔍 CreateIssueModal: Using project assignments (all project members):', projectUsers);
          setUsers(projectUsers);
        }
        if (!providedEpics) {
          const epicsData = await apiService.getEpics(projectId);
          setEpics(epicsData);
        }
        // Only load sprints for sprint-based boards (sprint, scrum)
        if (boardType !== 'bau' && boardType !== 'kanban') {
          const sprintsData = await apiService.getProjectSprints(projectId, true);
          setSprints(sprintsData.filter((s: any) => s.state !== 'completed' && s.id !== 'default-sprint'));
        } else {
          setSprints([]);
        }
        
        // Initialize parent issue list lazily (first page, no search)
        await fetchParentIssues('', 1, true);
      } catch (e) {
        // ignore
      } finally {
        setLoadingLists(false);
      }
    };
    if (open) loadData();
  }, [open, projectId, providedUsers, providedEpics, boardType]);

  // Fetch parent issues with pagination and optional search
  const fetchParentIssues = async (query: string, page: number, reset = false) => {
    if (!projectId) return;
    try {
      setParentLoading(true);
      const res = await apiService.getIssues({
        projectId,
        type: [IssueType.STORY, IssueType.TASK, IssueType.SUBTASK],
        search: query || undefined,
        page,
        limit: 50
      });
      const items = (res.data as unknown as Issue[]) || [];
      if (reset) {
        setParentOptions(items);
      } else {
        setParentOptions(prev => {
          const map = new Map(prev.map(i => [i.id, i]));
          items.forEach(i => map.set(i.id, i));
          return Array.from(map.values());
        });
      }
      const total = (res as any)?.pagination?.total;
      const currentCount = (reset ? items.length : (parentOptions.length + items.length));
      setParentHasMore(total ? currentCount < total : items.length === 50);
      setParentPage(page);
    } finally {
      setParentLoading(false);
    }
  };

  // Debounce search
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      fetchParentIssues(parentSearch, 1, true);
    }, 300);
    return () => clearTimeout(t);
  }, [parentSearch, open]);

  const updateField = (field: keyof CreateIssueData, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!form.title || !form.projectId) return;
    
    // Validate that bugs/defects have a parent story
    if (form.type === IssueType.BUG && !(form as any).parentId) {
      alert('Please select a parent story for this bug/defect.');
      return;
    }
    
    // Validate that due date is required for all issue types except Bugs
    if (form.type !== IssueType.BUG && (!form.dueDate || form.dueDate.trim() === '')) {
      alert('Due date is required for all issues except Bugs. Please select a due date.');
      return;
    }
    
    // Debug: Log the form data before sending
    console.log('🔍 Form data before submission:', form);
    console.log('🔍 Board type:', boardType);
    console.log('🔍 Sprint ID in form:', (form as any).sprintId);
    
    // Ensure no fake sprint IDs are sent
    const submitData = { ...form };
    if ((submitData as any).sprintId === 'default-sprint') {
      console.log('🔧 Removing fake sprint ID from submission');
      (submitData as any).sprintId = undefined;
    }
    
    try {
      setCreateLoading(true);
      const created = await apiService.createIssue(submitData);
      onCreated?.(created);
      onClose();
    } finally {
      setCreateLoading(false);
    }
  };

  const handleGenerateAI = async () => {
    if (!form.title) return;
    try {
      setGenLoading(true);
      const activeSprint = sprints.find(s => s.state === 'active');
      const epicName = epics.find(e => e.id === (form as any).epicId)?.name;
      const payload = {
        title: form.title,
        type: form.type,
        projectName: undefined,
        epicName,
        sprintName: activeSprint?.name,
        hint: ''
      } as any;
      const data = await apiService.generateStoryWithAI(payload);
      const ac = Array.isArray(data.acceptanceCriteria) ? data.acceptanceCriteria : [];
      const acMarkdown = ac.map((i) => `- ${i}`).join('\n');
      const combined = [data.description || '', ac.length ? `\n\nAcceptance Criteria:\n${acMarkdown}` : ''].filter(Boolean).join('\n');
      updateField('description', combined);
    } finally {
      setGenLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Issue</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Type dropdown in top left */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={form.type}
                onChange={(e) => updateField('type', e.target.value as IssueType)}
                label="Type"
                size="small"
              >
                <MenuItem value={IssueType.STORY}>Story</MenuItem>
                <MenuItem value={IssueType.TASK}>Task</MenuItem>
                <MenuItem value={IssueType.BUG}>Bug</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <TextField
            label="Title"
            fullWidth
            margin="normal"
            value={form.title}
            onChange={(e) => updateField('title', e.target.value)}
            required
          />

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Feature/Epic</InputLabel>
            <Select
              value={(form as any).epicId || ''}
              onChange={(e) => updateField('epicId' as any, e.target.value)}
              label="Feature/Epic"
            >
              <MenuItem value="">
                <em>No Feature</em>
              </MenuItem>
              {epics.length === 0 ? (
                <MenuItem disabled>
                  <em>No features available. Create features first.</em>
                </MenuItem>
              ) : (
                epics.map((epic) => (
                  <MenuItem key={epic.id} value={epic.id}>
                    {epic.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <TextField
            label="Description"
            fullWidth
            margin="normal"
            multiline
            rows={4}
            value={form.description}
            onChange={(e) => updateField('description', e.target.value)}
          />

          {/* Parent Issue Selection for Bugs/Defects */}
          {form.type === IssueType.BUG && (
            <Autocomplete
              options={parentOptions}
              loading={parentLoading}
              filterOptions={(x) => x} // disable client filtering; rely on server
              getOptionLabel={(option) => `${option.issueKey || option.id} - ${option.title}`}
              value={parentOptions.find(s => s.id === (form as any).parentId) || null}
              onChange={(_, newValue) => updateField('parentId' as any, newValue?.id || '')}
              onInputChange={(_, value) => setParentSearch(value)}
              ListboxProps={{
                onScroll: (e: any) => {
                  const el = e.currentTarget;
                  if (parentHasMore && !parentLoading && el.scrollTop + el.clientHeight >= el.scrollHeight - 24) {
                    fetchParentIssues(parentSearch, parentPage + 1);
                  }
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Parent Issue *"
                  margin="normal"
                  required
                  helperText="Search by issue ID or name (Story, Task, or Subtask)"
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Box sx={{ fontWeight: 'bold' }}>
                      {option.issueKey || option.id} 
                      <Chip 
                        label={option.type} 
                        size="small" 
                        sx={{ ml: 1, textTransform: 'capitalize' }}
                        color={option.type === IssueType.STORY ? 'primary' : 
                               option.type === IssueType.TASK ? 'secondary' : 'default'}
                      />
                    </Box>
                    <Box sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>{option.title}</Box>
                  </Box>
                </Box>
              )}
              noOptionsText="No issues found"
            />
          )}

          {/* Only show sprint selection for sprint-based boards */}
          {boardType !== 'bau' && boardType !== 'kanban' && (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Sprint</InputLabel>
              <Select
                value={(form as any).sprintId || ''}
                onChange={(e) => updateField('sprintId' as any, e.target.value)}
                label="Sprint"
              >
                <MenuItem value="">
                  <em>No Sprint</em>
                </MenuItem>
                {sprints.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name} ({s.state})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Priority</InputLabel>
            <Select
              value={form.priority}
              onChange={(e) => updateField('priority', e.target.value as Priority)}
              label="Priority"
            >
              <MenuItem value={Priority.LOW}>Low</MenuItem>
              <MenuItem value={Priority.MEDIUM}>Medium</MenuItem>
              <MenuItem value={Priority.HIGH}>High</MenuItem>
              <MenuItem value={Priority.CRITICAL}>Critical</MenuItem>
            </Select>
          </FormControl>

          <Autocomplete
            sx={{ mt: 2 }}
            options={[
              { id: '', firstName: 'Unassigned', lastName: '', email: '' },
              ...users
            ]}
            value={users.find(u => u.id === form.assigneeId) || { id: '', firstName: 'Unassigned', lastName: '', email: '' }}
            onChange={(_, newValue) => {
              updateField('assigneeId', newValue?.id || '');
            }}
            getOptionLabel={(option) => {
              if (option.id === '') return 'Unassigned';
              return `${option.firstName} ${option.lastName}`;
            }}
            disabled={loadingLists}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Assignee"
                placeholder="Search users..."
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem', bgcolor: option.id === '' ? 'grey.300' : 'primary.main' }}>
                    {option.id === '' ? (
                      <PersonIcon sx={{ fontSize: 16 }} />
                    ) : (
                      `${option.firstName?.[0]}${option.lastName?.[0]}`
                    )}
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography 
                      variant="body2"
                      sx={{ 
                        fontStyle: option.id === '' ? 'italic' : 'normal'
                      }}
                    >
                      {option.id === '' ? 'Unassigned' : `${option.firstName} ${option.lastName}`}
                    </Typography>
                    {option.id !== '' && (
                      <Typography variant="caption" color="text.secondary">
                        {option.email}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            )}
            filterOptions={(options, { inputValue }) => {
              if (!inputValue) return options;
              const searchLower = inputValue.toLowerCase();
              return options.filter(option => 
                option.id === '' || // Always show "Unassigned" option
                option.firstName.toLowerCase().includes(searchLower) ||
                option.lastName.toLowerCase().includes(searchLower) ||
                option.email.toLowerCase().includes(searchLower)
              );
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />

          <TextField
            label="Story Points"
            type="number"
            fullWidth
            margin="normal"
            value={form.storyPoints || ''}
            onChange={(e) => updateField('storyPoints', e.target.value ? parseInt(e.target.value, 10) : undefined)}
          />
          <TextField
            label="Original Estimate (hours)"
            type="number"
            fullWidth
            margin="normal"
            value={form.originalEstimate || ''}
            onChange={(e) => updateField('originalEstimate', e.target.value ? parseInt(e.target.value, 10) : undefined)}
          />
          <TextField
            label="Due Date"
            type="date"
            fullWidth
            required={form.type !== IssueType.BUG}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            value={form.dueDate || ''}
            onChange={(e) => updateField('dueDate', e.target.value)}
            error={form.type !== IssueType.BUG && !form.dueDate}
            helperText={form.type !== IssueType.BUG && !form.dueDate ? 'Due date is required' : (form.type === IssueType.BUG ? 'Optional for bugs' : '')}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleGenerateAI} disabled={!form.title || genLoading} color="secondary" startIcon={genLoading ? <CircularProgress size={20} /> : <SmartToyIcon />}>
          Generate with AI
        </Button>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSubmit} 
          variant="outlined" 
          disabled={createLoading}
          sx={{
            borderColor: '#64748b',
            color: '#334155',
            '&:hover': {
              borderColor: '#334155',
              backgroundColor: 'rgba(51, 65, 85, 0.04)',
            },
            '&:disabled': {
              borderColor: '#cbd5e1',
              color: '#94a3b8',
            }
          }}
        >
          {createLoading ? <CircularProgress size={20} /> : 'Create Issue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateIssueModal;


